import {FC} from "react";
import {
	Box,
	Stack,
	Switch,
	Button,
	Typography,
	LinearProgress,
	CircularProgress,
	Slider,
	Alert
} from "@mui/material";
import {AdminHeader} from "@/pages/ManagerView/AdminHeader";
import {useState, useEffect, useMemo} from "react";
import {Add, Delete, Edit} from "@mui/icons-material";
import {useBlocker} from "react-router-dom";
import {
	useGetCohortSettings,
	useUpdateCohortSettings,
	UpdateCohortSettingsBody,
	ExtendedAccessDto
} from "@/app2/shared/data";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {ConfirmUnsavedChangesDialog} from "./components/ConfirmUnsavedChangesDialog";
import {ConfirmClearEnrollmentsDialog} from "./components/ConfirmClearEnrollmentsDialog";
import {CohortLibraryImportModal} from "./components/CohortLibraryImportModal";
import {useOwnedCohortId} from "@/app2/shared/hooks";

/**
 * Admin Settings Page (App2)
 * 
 * Allows Training::Admin users to manage their cohort settings including:
 * - Daily lesson assignment limit (1-3)
 * - Training feed auto-assignment toggle
 * - Clear existing auto-enrollments option
 * - Comments enable/disable
 * - Publicly visible toggle
 * - Extended access library imports
 * - Pause streak feature toggle
 */
export const AdminSettingsPage: FC = () => {
	const {t} = useSSTranslation();
	const cohortId = useOwnedCohortId();

	// Fetch settings
	const {data: settings, isLoading, isError, error} = useGetCohortSettings(cohortId!, {
		enabled: !!cohortId
	});

	// Update mutation
	const {
		mutate: updateSettings,
		isPending: isUpdating
	} = useUpdateCohortSettings(cohortId!);

	// Local form state (uses response type for extendedAccess to have both id and title)
	const [formData, setFormData] = useState<UpdateCohortSettingsBody & {
		extendedAccess?: ExtendedAccessDto[];
	}>({});

	// Feature flags
	const {disableAutoAssign, pauseStreak} = useDevCycleSdk();

	// Modal states
	const [libraryModalOpen, setLibraryModalOpen] = useState(false);
	const [confirmUnsavedOpen, setConfirmUnsavedOpen] = useState(false);
	const [confirmClearOpen, setConfirmClearOpen] = useState(false);

	// Initialize form with settings data
	useEffect(() => {
		if (settings) {
			setFormData({
				dailyLimit: settings.dailyLimit,
				trainingFeedAutoAssignment: settings.trainingFeedAutoAssignment,
				comments: settings.comments,
				courseVariety: settings.courseVariety,
				publiclyVisible: settings.publiclyVisible,
				isPauseStreakEnabled: settings.isPauseStreakEnabled,
				extendedAccess: settings.extendedAccess
			});
		}
	}, [settings]);

	const {successSnackbar, errorSnackbar} = useCustomSnackbar();

	// Check for unsaved changes
	const hasChanges = useMemo(() => {
		if (!settings) return false;
		return (
			formData.dailyLimit !== settings.dailyLimit ||
      formData.trainingFeedAutoAssignment !== settings.trainingFeedAutoAssignment ||
      formData.comments !== settings.comments ||
      formData.courseVariety !== settings.courseVariety ||
      formData.publiclyVisible !== settings.publiclyVisible ||
      formData.isPauseStreakEnabled !== settings.isPauseStreakEnabled ||
      JSON.stringify(formData.extendedAccess) !== JSON.stringify(settings.extendedAccess) ||
      formData.clearExistingEnrollments === true
		);
	}, [formData, settings]);

	// Block navigation if there are unsaved changes
	const blocker = useBlocker(hasChanges);

	useEffect(() => {
		if (blocker.state === "blocked") {
			setConfirmUnsavedOpen(true);
		}
	}, [blocker]);

	// Beforeunload event
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (hasChanges) {
				event.preventDefault();
				event.returnValue = "";
				return true;
			}
			return false;
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [hasChanges]);

	const handleSave = () => {
		if (!hasChanges || isUpdating) return;

		// Transform extendedAccess to only include IDs for the API
		const payload: UpdateCohortSettingsBody = {
			...formData,
			extendedAccess: formData.extendedAccess?.map(ea => ({id: ea.id}))
		};
		if (formData.clearExistingEnrollments) {
			setConfirmClearOpen(true);
		} else {
			updateSettings(payload, {
				onSuccess: () => successSnackbar("Your details have been updated"),
				onError: () => errorSnackbar("Failed to save settings")
			});
		}
	};

	const handleDiscard = () => {
		if (settings) {
			setFormData({
				dailyLimit: settings.dailyLimit,
				trainingFeedAutoAssignment: settings.trainingFeedAutoAssignment,
				comments: settings.comments,
				courseVariety: settings.courseVariety,
				publiclyVisible: settings.publiclyVisible,
				isPauseStreakEnabled: settings.isPauseStreakEnabled,
				extendedAccess: settings.extendedAccess
			});
		}
	};

	const handleLibrarySelection = (selectedCohorts?: ExtendedAccessDto[]) => {
		if (selectedCohorts !== undefined) {
			setFormData(prev => ({...prev, extendedAccess: selectedCohorts}));
		}
		setLibraryModalOpen(false);
	};

	if (!cohortId) {
		return (
			<Box sx={{p: 3}}>
				<Alert severity="warning">
          You do not own a cohort to manage settings for.
				</Alert>
			</Box>
		);
	}

	if (isLoading) {
		return <LinearProgress />;
	}

	if (isError) {
		return (
			<Box sx={{p: 3}}>
				<Alert severity="error">
					{error?.message || "Failed to load settings"}
				</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{maxWidth: 1200, mx: "auto", p: 3}}>
			<AdminHeader title="Settings" />

			{/* Settings Form */}
			<Box
				sx={{
					borderRadius: 2,
					p: 3,
					border: 1,
					borderColor: "divider",
					bgcolor: "background.paper"
				}}
			>
				{/* My Feed Content Section */}
				<Box sx={{mb: 4}}>
					<Typography variant="h5" sx={{mb: 2}}>
            My Feed Content
					</Typography>

					{/* Auto Assignment Toggle */}
					{disableAutoAssign && (
						<Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
							<Switch
								checked={formData.trainingFeedAutoAssignment || false}
								onChange={e =>
									setFormData(prev => ({...prev, trainingFeedAutoAssignment: e.target.checked}))
								}
							/>
							<Typography>Auto assign relevant learning</Typography>
						</Stack>
					)}

					{/* Clear Enrollments Toggle */}
					{disableAutoAssign && formData.trainingFeedAutoAssignment === false && (
						<Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
							<Switch
								checked={formData.clearExistingEnrollments || false}
								onChange={e =>
									setFormData(prev => ({...prev, clearExistingEnrollments: e.target.checked}))
								}
							/>
							<Typography>
                Clear existing auto-enrollments
							</Typography>
						</Stack>
					)}

					{/* Pause Streak Toggle */}
					{pauseStreak && (
						<Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
							<Switch
								checked={formData.isPauseStreakEnabled || false}
								onChange={e =>
									setFormData(prev => ({...prev, isPauseStreakEnabled: e.target.checked}))
								}
							/>
							<Typography>
                Allow employees to pause their streak
							</Typography>
						</Stack>
					)}

					{/* Daily Limit Slider */}
					<Box sx={{maxWidth: 200, mt: 3}}>
						<Typography sx={{mb: 1}}>
              Daily assignment
						</Typography>
						<Slider
							value={formData.dailyLimit || 1}
							onChange={(_, value) => setFormData(prev => ({...prev, dailyLimit: value as number}))}
							min={1}
							max={3}
							step={1}
							marks={[1, 2, 3].map(v => ({value: v, label: v.toString()}))}
							valueLabelDisplay="auto"
						/>
					</Box>
				</Box>

				{/* Privacy and Content Sharing Section */}
				<Box sx={{mb: 4}}>
					<Typography variant="h5" sx={{mb: 2}}>
            Privacy and Content Sharing
					</Typography>

					<Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
						<Switch
							checked={formData.publiclyVisible || false}
							onChange={e => setFormData(prev => ({...prev, publiclyVisible: e.target.checked}))}
						/>
						<Typography>Publicly Visible</Typography>
					</Stack>
				</Box>

				{/* Comments Section */}
				<Box sx={{mb: 4}}>
					<Typography variant="h5" sx={{mb: 2}}>
            Comments
					</Typography>

					<Stack direction="row" spacing={1} alignItems="center">
						<Switch
							checked={formData.comments || false}
							onChange={e => setFormData(prev => ({...prev, comments: e.target.checked}))}
						/>
						<Typography>Allow Comments</Typography>
					</Stack>
				</Box>

				{/* Imported Libraries Section */}
				<Box sx={{mb: 4}}>
					<Box sx={{display: "flex", gap: 1, mb: 2}}>
						<Typography>
              Imported Libraries:
						</Typography>
						{(!formData.extendedAccess || formData.extendedAccess.length === 0) && (
							<Typography sx={{fontWeight: 600}}>
                none
							</Typography>
						)}
					</Box>

					{formData.extendedAccess && formData.extendedAccess.length > 0 && (
						<Box sx={{display: "flex", flexWrap: "wrap", gap: 2, mb: 2}}>
							{(formData.extendedAccess as ExtendedAccessDto[]).map(lib => (
								<Box
									key={lib.id}
									sx={{
										display: "flex",
										gap: 1,
										alignItems: "center",
										bgcolor: "grey.700",
										borderRadius: 1,
										p: 2
									}}
								>
									<Typography>{lib.title}</Typography>
									<Delete
										onClick={() =>
											setFormData(prev => ({
												...prev,
												extendedAccess: prev.extendedAccess?.filter(x => x.id !== lib.id) as ExtendedAccessDto[]
											}))
										}
										sx={{cursor: "pointer"}}
									/>
								</Box>
							))}
						</Box>
					)}

					<Button variant="contained" onClick={() => setLibraryModalOpen(true)} sx={{mt: 2}}>
						{formData.extendedAccess && formData.extendedAccess.length > 0
							? "Modify Selection"
							: "Add Library"}
						{formData.extendedAccess && formData.extendedAccess.length > 0 ? <Edit /> : <Add />}
					</Button>
				</Box>

				{/* Action Buttons */}
				<Box sx={{display: "flex", gap: 2, pt: 2, mt: 2, borderTop: 1, borderColor: "divider"}}>
					<Button
						variant="contained"
						disabled={!hasChanges || isUpdating}
						onClick={handleSave}
						sx={{minWidth: 190}}
					>
						{isUpdating ? <CircularProgress size={20} /> : "Save preferences"}
					</Button>

					<Button variant="contained" disabled={!hasChanges || isUpdating} onClick={handleDiscard}>
            Cancel changes
					</Button>
				</Box>
			</Box>

			{/* Modals */}
			{libraryModalOpen && (
				<CohortLibraryImportModal
					currentSelected={formData.extendedAccess?.map(e => e.id)}
					onClose={handleLibrarySelection}
				/>
			)}

			{confirmUnsavedOpen && (
				<ConfirmUnsavedChangesDialog
					onAccept={() => {
						blocker.proceed();
						setConfirmUnsavedOpen(false);
					}}
					onClose={() => {
						blocker.reset();
						setConfirmUnsavedOpen(false);
					}}
				/>
			)}

			{confirmClearOpen && (
				<ConfirmClearEnrollmentsDialog
					onAccept={() => {
						setConfirmClearOpen(false);
						const payload: UpdateCohortSettingsBody = {
							...formData,
							extendedAccess: formData.extendedAccess?.map(ea => ({id: ea.id}))
						};
						updateSettings(payload, {
							onSuccess: () => {
								setFormData(prev => ({...prev, clearExistingEnrollments: false}));
								successSnackbar("Auto-enrollments removed successfully");
							},
							onError: () => errorSnackbar("Failed to save settings")
						});
					}}
					onClose={() => setConfirmClearOpen(false)}
				/>
			)}
		</Box>
	);
};

